// src/entities/base.ts
var WiseId = class {
};

// src/entities/columns.ts
import { regions as regions2 } from "@researchwiseai/regions";

// src/errors/format.ts
var FormatError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "FormatError";
  }
};
function isFormatError(error) {
  return error instanceof Error && error.name === "FormatError";
}

// src/utils/parseInt.ts
function parseInt(input) {
  const s = String(input);
  const n = Number.parseInt(s);
  if (n.toString() === s && s === input.toString()) return n;
  return Number.NaN;
}

// src/utils/index.ts
var Utils = {
  parseInt
};

// src/entities/projects.ts
import { regions } from "@researchwiseai/regions";

// src/entities/organizations.ts
var OrganizationId = class _OrganizationId extends WiseId {
  _tenant;
  _region;
  _version;
  _id;
  static fromAuth0(id) {
    return new _OrganizationId(id);
  }
  static fromColon(colon) {
    const [tenant, version, region, id] = colon.split(":");
    return new _OrganizationId(id, tenant, region, version);
  }
  static fromPath(path) {
    const [tenant, version, region, id] = path.split("/");
    return new _OrganizationId(id, tenant, region, version);
  }
  static fromUrl(url) {
    try {
      const u = new URL(url);
      const [tenant, region] = u.hostname.split(".");
      const [, , version, id] = u.pathname.split("/");
      return new _OrganizationId(id, tenant, region, version);
    } catch (e) {
      throw new FormatError(`Invalid url: ${url}`);
    }
  }
  static isOrganizationId(obj) {
    return obj instanceof _OrganizationId;
  }
  static isOrganizationIdUrl(url) {
    try {
      const u = new URL(url);
      const [tenant, region] = u.hostname.split(".");
      const [, type, version, id] = u.pathname.split("/");
      return type === "organizations" && typeof tenant === "string" && typeof version === "string" && typeof region === "string" && typeof id === "string" && id.length > 0 && u.pathname === `/organizations/${version}/${id}`;
    } catch (e) {
      return false;
    }
  }
  constructor(id, tenant = process.env.AUTH0_TENANT ?? "", region = process.env.AUTH0_REGION ?? "eu", version = "v1") {
    super();
    this._id = id;
    this._tenant = tenant;
    this._region = region;
    this._version = version;
    this.validate();
  }
  get tenant() {
    return this._tenant;
  }
  get region() {
    return this._region;
  }
  get version() {
    return this._version;
  }
  get id() {
    return this._id;
  }
  toColon() {
    return `${this.tenant}:${this.version}:${this.region}:${this.id}`;
  }
  toUrl() {
    return `https://${this.tenant}.${this.region}.auth0.com/organizations/${this.version}/${this.id}`;
  }
  toPath() {
    return `${this.tenant}/${this.version}/${this.region}/${this.id}`;
  }
  validate() {
    if (!this.tenant) {
      throw new FormatError("tenant is required");
    }
    if (!this.region) {
      throw new FormatError("region is required");
    }
    if (!this.version) {
      throw new FormatError("version is required");
    }
    if (!this.id) {
      throw new FormatError("id is required");
    }
    if (typeof this.tenant !== "string") {
      throw new FormatError("tenant must be a string");
    }
    if (typeof this.region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this.version !== "string") {
      throw new FormatError("version must be a string");
    }
    if (typeof this.id !== "string") {
      throw new FormatError("id must be a string");
    }
  }
};

// src/entities/projects.ts
var V1ProjectId = class _V1ProjectId extends WiseId {
  _tenant;
  _region;
  _version = "v1";
  _id;
  static fromLocal(id, region) {
    return new _V1ProjectId(id, void 0, region);
  }
  static fromColon(colon) {
    const [tenant, version, region, id] = colon.split(":");
    if (version !== "v1") {
      throw new FormatError(`Invalid version: ${version}`);
    }
    return new _V1ProjectId(Utils.parseInt(id), tenant, region);
  }
  static fromUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, , version, region, id] = u.pathname.split("/");
      if (version !== "v1") {
        throw new FormatError(`Invalid version: ${version}`);
      }
      return new _V1ProjectId(Utils.parseInt(id), tenant, region);
    } catch (e) {
      throw new FormatError(`Invalid url: ${url}`);
    }
  }
  static fromPath(path) {
    const [tenant, version, region, id] = path.split("/");
    if (version !== "v1") {
      throw new FormatError(`Invalid version: ${version}`);
    }
    return new _V1ProjectId(Utils.parseInt(id), tenant, region);
  }
  static isProjectId(obj) {
    return obj instanceof _V1ProjectId;
  }
  static isProjectIdUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, type, version, region, id] = u.pathname.split("/");
      return type === "projects" && typeof tenant === "string" && typeof version === "string" && typeof region === "string" && typeof id === "string" && id.length > 0 && Object.keys(regions).includes(region) && u.pathname === `/projects/${version}/${region}/${id}`;
    } catch (e) {
      return false;
    }
  }
  constructor(id, tenant = process.env.TENANT ?? "researchwiseai", region = "eu") {
    super();
    this._id = id;
    this._tenant = tenant;
    this._region = region;
    this.validate();
  }
  get tenant() {
    return this._tenant;
  }
  get region() {
    return this._region;
  }
  get version() {
    return this._version;
  }
  get id() {
    return this._id;
  }
  toUrl() {
    return `https://${this.tenant}.com/projects/${this.version}/${this.region}/${this.id}`;
  }
  toPath() {
    return `${this.tenant}/${this.version}/${this.region}/${this.id}`;
  }
  toColon() {
    return `${this.tenant}:${this.version}:${this.region}:${this.id}`;
  }
  validate() {
    if (!this.tenant) {
      throw new FormatError("tenant is required");
    }
    if (!this.region) {
      throw new FormatError("region is required");
    }
    if (!this.id) {
      throw new FormatError("id is required");
    }
    if (typeof this.tenant !== "string") {
      throw new FormatError("tenant must be a string");
    }
    if (typeof this.region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this.id !== "number" || Number.isNaN(this.id) || !Number.isInteger(this.id)) {
      throw new FormatError("id must be an integer");
    }
    if (!Object.keys(regions).includes(this._region)) {
      throw new FormatError(`region must be one of ${Object.keys(regions).join(", ")}`);
    }
  }
};
var V2ProjectId = class _V2ProjectId extends WiseId {
  _region;
  _version = "v2";
  _id;
  _organizationId;
  constructor({
    id,
    organizationId,
    region
  }) {
    super();
    this._id = id;
    this._organizationId = organizationId;
    this._region = region;
    this.validate();
  }
  static fromColon(colon) {
    const [organizationTenant, organizationId, version, region, id] = colon.split(":");
    if (version !== "v2") {
      throw new FormatError(`Invalid version: ${version}`);
    }
    return new _V2ProjectId({
      id,
      organizationId: new OrganizationId(organizationId, organizationTenant),
      region
    });
  }
  static fromUrl(url) {
    const regexp = new RegExp(
      "https://(?<orgId>[a-zA-Z0-9-_]+).(?<orgTenant>[a-zA-Z0-9-]+).com/projects/v2/(?<region>[a-zA-Z0-9-]+)/(?<projectId>[a-zA-Z0-9-]+)",
      "g"
    );
    try {
      const results = url.matchAll(regexp);
      const result = [...results][0];
      if (result == null) {
        throw new FormatError(`Invalid url: ${url}`);
      }
      const organizationId = result.groups?.orgId;
      const organizationTenant = result.groups?.orgTenant;
      const region = result.groups?.region;
      const id = result.groups?.projectId;
      if (organizationId == null || organizationTenant == null || region == null || id == null) {
        throw new FormatError(`Invalid url: ${url}`);
      }
      return new _V2ProjectId({
        id,
        organizationId: new OrganizationId(organizationId, organizationTenant),
        region
      });
    } catch (e) {
      if (e instanceof FormatError) {
        throw e;
      }
      {
        if (e instanceof Error) {
          console.error(
            {
              url,
              error: {
                message: e.message,
                stack: e.stack,
                name: e.name
              }
            },
            "Failed to parse project id from url"
          );
        }
        throw new FormatError(`Invalid url: ${url}`);
      }
    }
  }
  static fromPath(path) {
    const [organizationTenant, organizationId, version, region, id] = path.split("/");
    if (version !== "v2") {
      throw new FormatError(`Invalid version: ${version}`);
    }
    return new _V2ProjectId({
      id,
      organizationId: new OrganizationId(organizationId, organizationTenant),
      region
    });
  }
  static isProjectId(obj) {
    return obj instanceof _V2ProjectId;
  }
  get region() {
    return this._region;
  }
  get version() {
    return this._version;
  }
  get id() {
    return this._id;
  }
  get organizationId() {
    return this._organizationId;
  }
  toUrl() {
    return `https://${this._organizationId.id}.${this._organizationId.tenant}.com/projects/${this.version}/${this.region}/${this.id}`;
  }
  toPath() {
    return `${this._organizationId.tenant}/${this._organizationId.id}/${this.version}/${this.region}/${this.id}`;
  }
  toColon() {
    return `${this._organizationId.tenant}:${this._organizationId.id}:${this.version}:${this.region}:${this.id}`;
  }
  validate() {
    if (!this._organizationId) {
      throw new FormatError("organizationId is required");
    }
    if (!this.region) {
      throw new FormatError("region is required");
    }
    if (!this.id) {
      throw new FormatError("id is required");
    }
    if (typeof this.region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this.id !== "string") {
      throw new FormatError("id must be a string");
    }
    if (!Object.keys(regions).includes(this._region)) {
      throw new FormatError(`region must be one of ${Object.keys(regions).join(", ")}`);
    }
  }
};
var V1AndV2ProjectId = class {
  _tenant;
  _region;
  _idV1;
  _idV2;
  _organizationId;
  constructor({
    idV1,
    idV2,
    organizationId,
    region,
    tenant
  }) {
    this._idV1 = idV1;
    this._idV2 = idV2;
    this._organizationId = organizationId;
    this._region = region;
    this._tenant = tenant;
    this.validate();
  }
  get tenant() {
    return this._tenant;
  }
  get region() {
    return this._region;
  }
  get idV1() {
    return this._idV1;
  }
  get idV2() {
    return this._idV2;
  }
  get organizationId() {
    return this._organizationId;
  }
  asV1() {
    return new V1ProjectId(this.idV1, this.tenant, this.region);
  }
  asV2() {
    return new V2ProjectId({
      id: this.idV2,
      organizationId: this.organizationId,
      region: this.region
    });
  }
  validate() {
    if (!this.organizationId) {
      throw new FormatError("organizationId is required");
    }
    if (!this.region) {
      throw new FormatError("region is required");
    }
    if (!this.idV1) {
      throw new FormatError("idV1 is required");
    }
    if (!this.idV2) {
      throw new FormatError("idV2 is required");
    }
    if (typeof this.region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this.idV1 !== "number" || Number.isNaN(this.idV1) || !Number.isInteger(this.idV1)) {
      throw new FormatError("idV1 must be an integer");
    }
    if (typeof this.idV2 !== "string") {
      throw new FormatError("idV2 must be a string");
    }
    if (!Object.keys(regions).includes(this._region)) {
      throw new FormatError(`region must be one of ${Object.keys(regions).join(", ")}`);
    }
  }
};
var ProjectId = {
  fromLocal(id, region) {
    return new V1ProjectId(id, void 0, region);
  },
  fromColon(colon) {
    if (colon.includes(":v1:")) {
      const [tenant, version, region, id] = colon.split(":");
      if (version !== "v1") {
        throw new FormatError(`Invalid version: ${version}`);
      }
      return new V1ProjectId(Utils.parseInt(id), tenant, region);
    } else if (colon.includes(":v2:")) {
      const [organizationTenant, organizationId, version, region, id] = colon.split(":");
      if (version !== "v2") {
        throw new FormatError(`Invalid version: ${version}`);
      }
      return new V2ProjectId({
        id,
        organizationId: new OrganizationId(organizationId, organizationTenant),
        region
      });
    } else {
      throw new FormatError(`Invalid version`);
    }
  },
  fromUrl(url) {
    try {
      const u = new URL(url);
      const [, , version, region, id] = u.pathname.split("/");
      if (version === "v1") {
        const [tenant] = u.hostname.split(".");
        return new V1ProjectId(Utils.parseInt(id), tenant, region);
      } else if (version === "v2") {
        return V2ProjectId.fromUrl(url);
      } else {
        throw new FormatError(`Invalid version: ${version}`);
      }
    } catch (e) {
      throw new FormatError(`Invalid url: ${url}`);
    }
  },
  fromPath(path) {
    if (path.includes("/v1/")) {
      const [tenant, version, region, id] = path.split("/");
      if (version !== "v1") {
        throw new FormatError(`Invalid version: ${version}`);
      }
      return new V1ProjectId(Utils.parseInt(id), tenant, region);
    } else if (path.includes("/v2/")) {
      const [organizationTenant, organizationId, version, region, id] = path.split("/");
      if (version !== "v2") {
        throw new FormatError(`Invalid version: ${version}`);
      }
      return new V2ProjectId({
        id,
        organizationId: new OrganizationId(organizationId, organizationTenant),
        region
      });
    } else {
      throw new FormatError(`Invalid version`);
    }
  },
  isProjectId(obj) {
    return obj instanceof V1ProjectId || obj instanceof V2ProjectId;
  },
  isV1ProjectId(obj) {
    return obj instanceof V1ProjectId;
  },
  isV2ProjectId(obj) {
    return obj instanceof V2ProjectId;
  },
  isProjectIdUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, type, version, region, id] = u.pathname.split("/");
      return type === "projects" && typeof tenant === "string" && typeof version === "string" && (version === "v1" || version === "v2") && typeof region === "string" && typeof id === "string" && id.length > 0 && Object.keys(regions).includes(region) && u.pathname === `/projects/${version}/${region}/${id}`;
    } catch (e) {
      return false;
    }
  }
};

// src/entities/columns.ts
var ColumnId = class _ColumnId extends WiseId {
  _tenant;
  _region;
  _version;
  _id;
  static fromColon(colon) {
    const [tenant, version, region, id] = colon.split(":");
    return new _ColumnId(Utils.parseInt(id), tenant, region, version);
  }
  static fromLocal(id, region) {
    return new _ColumnId(id, void 0, region);
  }
  static fromUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, , version, region, id] = u.pathname.split("/");
      return new _ColumnId(Utils.parseInt(id), tenant, region, version);
    } catch (e) {
      if (e instanceof Error) {
        console.error(
          {
            url,
            error: {
              message: e.message,
              stack: e.stack,
              name: e.name
            }
          },
          "Invalid url"
        );
      }
      throw new FormatError(`Invalid url: ${url}`);
    }
  }
  static fromPath(path) {
    const [tenant, version, region, id] = path.split("/");
    return new _ColumnId(Utils.parseInt(id), tenant, region, version);
  }
  static isColumnId(obj) {
    return obj instanceof _ColumnId;
  }
  static isColumnIdUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, type, version, region, id] = u.pathname.split("/");
      return type === "columns" && typeof tenant === "string" && typeof version === "string" && typeof region === "string" && typeof id === "string" && id.length > 0 && Object.keys(regions2).includes(region) && u.pathname === `/columns/${version}/${region}/${id}`;
    } catch (e) {
      return false;
    }
  }
  constructor(id, tenant = process.env.TENANT ?? "researchwiseai", region = "eu", version = "v1") {
    super();
    this._id = id;
    this._tenant = tenant;
    this._region = region;
    this._version = version;
    this.validate();
  }
  get tenant() {
    return this._tenant;
  }
  get region() {
    return this._region;
  }
  get version() {
    return this._version;
  }
  get id() {
    return this._id;
  }
  toColon() {
    return `${this.tenant}:${this.version}:${this.region}:${this.id}`;
  }
  toPath() {
    return `${this.tenant}/${this.version}/${this.region}/${this.id}`;
  }
  toUrl() {
    return `https://${this.tenant}.com/columns/${this.version}/${this.region}/${this.id}`;
  }
  validate() {
    if (!this.id) {
      throw new FormatError("id is required");
    }
    if (typeof this.tenant !== "string") {
      throw new FormatError("tenant must be a string");
    }
    if (typeof this.region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this.version !== "string") {
      throw new FormatError("version must be a string");
    }
    if (typeof this.id !== "number" || Number.isNaN(this.id)) {
      throw new FormatError("id must be an integer");
    }
    if (!Object.keys(regions2).includes(this._region)) {
      throw new FormatError(`region must be one of ${Object.keys(regions2).join(", ")}`);
    }
  }
};
var V2ColumnId = class _V2ColumnId extends WiseId {
  _projectId;
  _id;
  static fromColon(colon) {
    const [orgTenant, orgId, version, region, projectId, id] = colon.split(":");
    if (version !== "v2") {
      throw new FormatError(`Invalid version: ${version}`);
    }
    return new _V2ColumnId({
      projectId: new V2ProjectId({
        id: projectId,
        organizationId: new OrganizationId(orgId, orgTenant),
        region
      }),
      columnId: id
    });
  }
  static fromUrl(url) {
    const regexp = new RegExp(
      "https://(?<orgId>[a-zA-Z0-9-_]+).(?<orgTenant>[a-zA-Z0-9-]+).com/projects/v2/(?<region>[a-zA-Z0-9-]+)/(?<projectId>[a-zA-Z0-9-]+)/columns/(?<columnId>[a-zA-Z0-9-]+)",
      "g"
    );
    try {
      const results = url.matchAll(regexp);
      const result = [...results][0];
      if (result == null) {
        throw new FormatError(`Invalid url: ${url}`);
      }
      const organizationId = result.groups?.orgId;
      const organizationTenant = result.groups?.orgTenant;
      const region = result.groups?.region;
      const id = result.groups?.projectId;
      const columnId = result.groups?.columnId;
      if (organizationId == null || organizationTenant == null || region == null || id == null || columnId == null) {
        throw new FormatError(`Invalid url: ${url}`);
      }
      const projectId = new V2ProjectId({
        id,
        organizationId: new OrganizationId(organizationId, organizationTenant),
        region
      });
      return new _V2ColumnId({
        projectId,
        columnId
      });
    } catch (e) {
      if (e instanceof Error) {
        console.error(
          {
            url,
            error: {
              message: e.message,
              stack: e.stack,
              name: e.name
            }
          },
          "Invalid url"
        );
      }
      throw new FormatError(`Invalid url: ${url}`);
    }
  }
  static isColumnId(obj) {
    return obj instanceof _V2ColumnId;
  }
  constructor({ columnId, projectId }) {
    super();
    this._id = columnId;
    this._projectId = projectId;
    this.validate();
  }
  get id() {
    return this._id;
  }
  get projectId() {
    return this._projectId;
  }
  toColon() {
    return `${this.projectId.toColon()}:${this.id}`;
  }
  toUrl() {
    return `${this.projectId.toUrl()}/columns/${this.id}`;
  }
  validate() {
    if (!this.id) {
      throw new FormatError("id is required");
    }
    if (typeof this.id !== "string") {
      throw new FormatError("is must be a string");
    }
  }
};

// src/entities/jobs.ts
import { regions as regions3 } from "@researchwiseai/regions";
var JobId = class _JobId extends WiseId {
  _tenant;
  _region;
  _version;
  _id;
  static fromColon(colon) {
    const [tenant, version, region, id] = colon.split(":");
    return new _JobId(id, tenant, region, version);
  }
  static fromLocal(id, region) {
    return new _JobId(id, void 0, region);
  }
  static fromUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, , version, region, id] = u.pathname.split("/");
      return new _JobId(id, tenant, region, version);
    } catch (e) {
      throw new FormatError(`Invalid url: ${url}`);
    }
  }
  static fromPath(path) {
    const [tenant, version, region, id] = path.split("/");
    return new _JobId(id, tenant, region, version);
  }
  static isJobId(obj) {
    return obj instanceof _JobId;
  }
  static isJobIdUrl(url) {
    try {
      const u = new URL(url);
      const [tenant] = u.hostname.split(".");
      const [, type, version, region, id] = u.pathname.split("/");
      return type === "jobs" && typeof tenant === "string" && typeof version === "string" && typeof region === "string" && typeof id === "string" && id.length === 36 && Object.keys(regions3).includes(region) && u.pathname === `/jobs/${version}/${region}/${id}`;
    } catch (e) {
      return false;
    }
  }
  constructor(id, tenant = process.env.TENANT ?? "researchwiseai", region = "eu", version = "v1") {
    super();
    this._id = id;
    this._tenant = tenant;
    this._region = region;
    this._version = version;
    this.validate();
  }
  get tenant() {
    return this._tenant;
  }
  get region() {
    return this._region;
  }
  get version() {
    return this._version;
  }
  get id() {
    return this._id;
  }
  toColon() {
    return `${this.tenant}:${this.version}:${this.region}:${this.id}`;
  }
  toPath() {
    return `${this.tenant}/${this.version}/${this.region}/${this.id}`;
  }
  toUrl() {
    return `https://${this.tenant}.com/jobs/${this.version}/${this.region}/${this.id}`;
  }
  validate() {
    if (!this.id) {
      throw new FormatError("id is required");
    }
    if (typeof this.tenant !== "string") {
      throw new FormatError("tenant must be a string");
    }
    if (typeof this.region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this.version !== "string") {
      throw new FormatError("version must be a string");
    }
    if (typeof this.id !== "string") {
      throw new FormatError("id must be a string");
    }
    if (!Object.keys(regions3).includes(this._region)) {
      throw new FormatError(`region must be one of ${Object.keys(regions3).join(", ")}`);
    }
  }
};

// src/entities/users.ts
var UserId = class _UserId extends WiseId {
  _tenant;
  _region;
  _version;
  _id;
  static fromAuth0(id) {
    return new _UserId(id);
  }
  static fromColon(colon) {
    const [tenant, version, region, id] = colon.split(":");
    return new _UserId(id, tenant, region, version);
  }
  static fromUrl(url) {
    try {
      const u = new URL(url);
      const [tenant, region] = u.hostname.split(".");
      const [, , version, id] = u.pathname.split("/");
      return new _UserId(id, tenant, region, version);
    } catch (e) {
      throw new FormatError(`Invalid url: ${url}`);
    }
  }
  static fromPath(path) {
    const [tenant, version, region, id] = path.split("/");
    return new _UserId(id, tenant, region, version);
  }
  static isUserId(obj) {
    return obj instanceof _UserId;
  }
  static isUserIdUrl(url) {
    try {
      const u = new URL(url);
      const [tenant, region] = u.hostname.split(".");
      const [, type, version, id] = u.pathname.split("/");
      return type === "users" && typeof tenant === "string" && typeof version === "string" && typeof region === "string" && typeof id === "string" && id.length > 0 && u.pathname === `/users/${version}/${id}`;
    } catch (e) {
      return false;
    }
  }
  constructor(id, tenant = process.env.AUTH0_TENANT ?? process.env.NEXT_PUBLIC_AUTH0_TENANT ?? "", region = "eu", version = "v1") {
    super();
    this._id = id;
    this._tenant = tenant;
    this._region = region;
    this._version = version;
    this.validate();
  }
  get tenant() {
    return this._tenant;
  }
  get region() {
    return this._region;
  }
  get version() {
    return this._version;
  }
  get id() {
    return this._id;
  }
  toColon() {
    return `${this.tenant}:${this.version}:${this.region}:${this.id}`;
  }
  toUrl() {
    return `https://${this.tenant}.${this.region}.auth0.com/users/${this.version}/${this.id}`;
  }
  toPath() {
    return `${this.tenant}/${this.version}/${this.region}/${this.id}`;
  }
  toString() {
    return this.toUrl();
  }
  validate() {
    if (!this._tenant) {
      throw new FormatError("tenant is required");
    }
    if (!this._region) {
      throw new FormatError("region is required");
    }
    if (!this._version) {
      throw new FormatError("version is required");
    }
    if (!this._id) {
      throw new FormatError("id is required");
    }
    if (typeof this._tenant !== "string") {
      throw new FormatError("tenant must be a string");
    }
    if (typeof this._region !== "string") {
      throw new FormatError("region must be a string");
    }
    if (typeof this._version !== "string") {
      throw new FormatError("version must be a string");
    }
    if (typeof this._id !== "string") {
      throw new FormatError("id must be a string");
    }
  }
};
export {
  ColumnId,
  FormatError,
  JobId,
  OrganizationId,
  ProjectId,
  UserId,
  V1AndV2ProjectId,
  V1ProjectId,
  V2ColumnId,
  V2ProjectId,
  WiseId,
  isFormatError
};
